import { useMemo, type ReactNode } from 'react'
import { AuthContext, type AuthContextProps } from 'react-oidc-context'

// E2E test-mode bypass.
//
// When the Playwright harness flips `sessionStorage['e2e-test-mode'] = '1'`
// before the first navigation, the root component renders `<TestModeAuthShim>`
// in place of the real `AuthProvider`. The shim provides a fake OIDC
// context where `useAuth()` reports the seeded admin user as signed-in,
// so the rest of the tree (CurrentUserProvider, AuthGate, AppSidebar's
// signout UI, etc.) works unchanged — no Keycloak round-trip required.
//
// Safety: this file only renders the shim when BOTH gates are true:
//   1. Build-time: `import.meta.env.VITE_E2E_TEST_MODE === '1'` — the
//      prod build is compiled without it, so the branch is dead-code
//      eliminated from the bundle.
//   2. Runtime: `sessionStorage['e2e-test-mode'] === '1'` — the flag must
//      be explicitly set on the page, which only happens in Playwright.
//
// The matching DB admin is the one seeded by `tests/helpers/pat.ts` — its
// UUID is `11111111-1111-1111-1111-111111111111`, so the `syncCurrentUser`
// RPC finds the existing user and refreshes the profile without creating a
// new one.

const TEST_ADMIN_ID = '11111111-1111-1111-1111-111111111111'
const TEST_ADMIN_EMAIL = 'test-admin@rapid-response.test'
const TEST_ADMIN_NAME = 'E2E Test Admin'

export function isTestModeEnabled(): boolean {
  if (import.meta.env.VITE_E2E_TEST_MODE !== '1') return false
  if (import.meta.env.SSR) return false
  try {
    return globalThis.sessionStorage.getItem('e2e-test-mode') === '1'
  } catch {
    return false
  }
}

export function TestModeAuthShim({ children }: Readonly<{ children: ReactNode }>) {
  // The shape here mirrors `AuthContextProps` — most methods are no-ops
  // because the shim never signs in/out; the only field the app actually
  // reads is `user.profile` plus `isAuthenticated`.
  const value = useMemo(
    () =>
      ({
        user: {
          profile: {
            sub: TEST_ADMIN_ID,
            email: TEST_ADMIN_EMAIL,
            name: TEST_ADMIN_NAME,
          },
          access_token: 'e2e-test-mode',
          token_type: 'Bearer',
          scope: 'openid profile email',
          expires_at: Math.floor(Date.now() / 1000) + 3600,
        },
        isLoading: false,
        isAuthenticated: true,
        settings: {},
        events: {},
        clearStaleState: async () => {},
        removeUser: async () => {},
        signinPopup: async () => {
          throw new Error('signinPopup not supported in test mode')
        },
        signinSilent: async () => null,
        signinRedirect: async () => {},
        signinResourceOwnerCredentials: async () => {
          throw new Error('signinResourceOwnerCredentials not supported in test mode')
        },
        signoutRedirect: async () => {},
        signoutPopup: async () => {},
        signoutSilent: async () => {},
        querySessionStatus: async () => null,
        revokeTokens: async () => {},
        startSilentRenew: () => {},
        stopSilentRenew: () => {},
      }) as unknown as AuthContextProps,
    [],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
